import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type ReactNode,
} from "react";

import borderDark from "@/assets/figma/left-navigation/border-dark.svg";
import borderLight from "@/assets/figma/left-navigation/border-light.svg";
import chatHistory from "@/assets/figma/left-navigation/chat-history.svg";
import dataOverview from "@/assets/figma/left-navigation/data-overview.svg";
import divider from "@/assets/figma/left-navigation/divider.svg";
import home from "@/assets/figma/left-navigation/home.svg";
import myFiles from "@/assets/figma/left-navigation/my-files.svg";
import newChat from "@/assets/figma/left-navigation/new-chat.svg";
import notification from "@/assets/figma/left-navigation/notification.svg";
import { cn } from "@/shared/lib/cn";
import { MerryButton } from "@/shared/ui/MerryButton";
import { MerryChatLabel } from "@/shared/ui/MerryChatLabel";
import {
  getMerryChatIndicatorAccessibleLabel,
  type MerryChatNavigationIndicator,
} from "@/shared/ui/merryChatActivityModel";
import {
  isMerryLeftNavigationRouteItem,
  merryLeftNavigationDividerNodeIds,
  merryLeftNavigationSections,
  type MerryLeftNavigationItemId,
  type MerryLeftNavigationRouteItemId,
} from "@/shared/ui/merryLeftNavigationItems";

gsap.registerPlugin(useGSAP);

const iconSources: Record<MerryLeftNavigationItemId, string> = {
  newChat,
  chatHistory,
  home,
  dataOverview,
  myFiles,
  notifications: notification,
};

export interface MerryLeftNavigationProps {
  "data-testid"?: string;
  activeItem: MerryLeftNavigationRouteItemId;
  ariaLabel?: string;
  chatHistoryDrawer?: ReactNode;
  chatHistoryIndicator?: MerryChatNavigationIndicator;
  notificationIndicator?: MerryChatNavigationIndicator;
  className?: string;
  itemTestIdPrefix?: string;
  onNavigate: (item: MerryLeftNavigationRouteItemId) => void;
  onNewChat: () => void;
}

export function MerryLeftNavigation({
  "data-testid": testId,
  activeItem,
  ariaLabel = "登录后主导航",
  chatHistoryDrawer,
  chatHistoryIndicator = { type: "none" },
  notificationIndicator = { type: "none" },
  className,
  itemTestIdPrefix = "left-navigation-item",
  onNavigate,
  onNewChat,
}: MerryLeftNavigationProps) {
  const chatHistoryItemRef = useRef<HTMLDivElement>(null);
  const chatHistoryDrawerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const suppressNextFocusOpenRef = useRef(false);
  const drawerId = useId();
  const [isChatHistoryDrawerOpen, setIsChatHistoryDrawerOpen] = useState(false);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const openChatHistoryDrawer = useCallback(() => {
    if (!chatHistoryDrawer) return;
    clearCloseTimer();
    setIsChatHistoryDrawerOpen(true);
  }, [chatHistoryDrawer, clearCloseTimer]);

  const scheduleChatHistoryDrawerClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsChatHistoryDrawerOpen(false);
      closeTimerRef.current = null;
    }, 160);
  }, [clearCloseTimer]);

  const closeChatHistoryDrawerAndRestoreFocus = useCallback(() => {
    clearCloseTimer();
    setIsChatHistoryDrawerOpen(false);
    const trigger = chatHistoryItemRef.current?.querySelector<HTMLButtonElement>(
      "button",
    );
    if (trigger && document.activeElement !== trigger) {
      suppressNextFocusOpenRef.current = true;
      trigger.focus();
    }
  }, [clearCloseTimer]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  useEffect(() => {
    if (!isChatHistoryDrawerOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeChatHistoryDrawerAndRestoreFocus();
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [closeChatHistoryDrawerAndRestoreFocus, isChatHistoryDrawerOpen]);

  useGSAP(
    () => {
      const drawer = chatHistoryDrawerRef.current?.firstElementChild;
      if (!(drawer instanceof HTMLElement)) return;

      gsap.killTweensOf(drawer);
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(drawer, {
          autoAlpha: isChatHistoryDrawerOpen ? 1 : 0,
          x: 0,
        });
        return;
      }

      gsap.to(drawer, {
        autoAlpha: isChatHistoryDrawerOpen ? 1 : 0,
        duration: isChatHistoryDrawerOpen ? 0.28 : 0.18,
        ease: isChatHistoryDrawerOpen ? "power3.out" : "power2.in",
        overwrite: true,
        x: isChatHistoryDrawerOpen ? 0 : -12,
      });
    },
    {
      dependencies: [isChatHistoryDrawerOpen],
      scope: chatHistoryItemRef,
    },
  );

  const handleChatHistoryBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    scheduleChatHistoryDrawerClose();
  };

  const handleChatHistoryFocus = () => {
    if (suppressNextFocusOpenRef.current) {
      suppressNextFocusOpenRef.current = false;
      return;
    }
    openChatHistoryDrawer();
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={cn("merry-left-navigation", className)}
      data-figma-node-id="299:31085"
      data-testid={testId}
    >
      <span aria-hidden="true" className="merry-left-navigation__edge">
        <img
          alt=""
          className="merry-left-navigation__edge-line merry-left-navigation__edge-line--light"
          src={borderLight}
        />
        <img
          alt=""
          className="merry-left-navigation__edge-line merry-left-navigation__edge-line--dark"
          src={borderDark}
        />
      </span>

      {merryLeftNavigationSections.map((section, sectionIndex) => (
        <Fragment key={section[0].id}>
          {section.map((item) => {
            const isRoute = isMerryLeftNavigationRouteItem(item);
            const isActive = isRoute && item.id === activeItem;

            const isChatHistory = item.id === "chatHistory";
            const itemIndicator = isChatHistory
              ? chatHistoryIndicator
              : item.id === "notifications"
                ? notificationIndicator
                : { type: "none" as const };
            const statusLabel =
              item.id === "notifications" && itemIndicator.type === "count"
                ? `有 ${itemIndicator.count} 条未读通知`
                : item.id === "notifications" && itemIndicator.type === "dot"
                  ? "有未读通知"
                  : isChatHistory
                    ? getMerryChatIndicatorAccessibleLabel(itemIndicator)
                    : null;

            return (
              <div
                className="merry-left-navigation__item"
                data-figma-node-id={item.figmaNodeId}
                key={item.id}
                onBlurCapture={isChatHistory ? handleChatHistoryBlur : undefined}
                onFocusCapture={
                  isChatHistory ? handleChatHistoryFocus : undefined
                }
                onPointerEnter={
                  isChatHistory ? openChatHistoryDrawer : undefined
                }
                onPointerLeave={
                  isChatHistory ? scheduleChatHistoryDrawerClose : undefined
                }
                ref={isChatHistory ? chatHistoryItemRef : undefined}
              >
                <MerryButton
                  aria-current={isActive ? "page" : undefined}
                  aria-controls={
                    isChatHistory && chatHistoryDrawer ? drawerId : undefined
                  }
                  aria-expanded={
                    isChatHistory && chatHistoryDrawer
                      ? isChatHistoryDrawerOpen
                      : undefined
                  }
                  aria-label={
                    statusLabel ? `${item.label}，${statusLabel}` : item.label
                  }
                  className="merry-left-navigation__button"
                  data-left-navigation-active={isActive ? "true" : "false"}
                  data-left-navigation-item={item.id}
                  data-testid={`${itemTestIdPrefix}-${item.id}`}
                  hierarchy="navigation"
                  icon={
                    <img
                      alt=""
                      aria-hidden="true"
                      className={cn(
                        "merry-left-navigation__icon",
                        `merry-left-navigation__icon--${item.id}`,
                      )}
                      src={iconSources[item.id]}
                    />
                  }
                  onPress={() => {
                    if (isRoute) {
                      onNavigate(item.id);
                    } else {
                      onNewChat();
                    }
                  }}
                  size={48}
                />

                {isChatHistory && chatHistoryIndicator.type !== "none" ? (
                  <span className="merry-left-navigation__chat-indicator">
                    {chatHistoryIndicator.type === "count" ? (
                      <MerryChatLabel
                        count={chatHistoryIndicator.count}
                        size={14}
                        type="count"
                      />
                    ) : (
                      <MerryChatLabel
                        size={14}
                        type={chatHistoryIndicator.type}
                      />
                    )}
                  </span>
                ) : null}

                {item.id === "notifications" &&
                notificationIndicator.type !== "none" ? (
                  <span className="merry-left-navigation__chat-indicator">
                    {notificationIndicator.type === "count" ? (
                      <MerryChatLabel
                        count={notificationIndicator.count}
                        size={14}
                        type="count"
                      />
                    ) : (
                      <MerryChatLabel
                        size={14}
                        type={notificationIndicator.type}
                      />
                    )}
                  </span>
                ) : null}

                {isChatHistory && chatHistoryDrawer ? (
                  <div
                    aria-hidden={!isChatHistoryDrawerOpen || undefined}
                    className="merry-left-navigation__chat-drawer"
                    data-open={isChatHistoryDrawerOpen ? "true" : "false"}
                    id={drawerId}
                    inert={!isChatHistoryDrawerOpen || undefined}
                    ref={chatHistoryDrawerRef}
                  >
                    {chatHistoryDrawer}
                  </div>
                ) : null}
              </div>
            );
          })}

          {sectionIndex < merryLeftNavigationDividerNodeIds.length ? (
            <span
              aria-hidden="true"
              className="merry-left-navigation__divider"
              data-figma-node-id={
                merryLeftNavigationDividerNodeIds[sectionIndex]
              }
            >
              <img alt="" src={divider} />
            </span>
          ) : null}
        </Fragment>
      ))}
    </nav>
  );
}
